import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import useWebSocket, { ReadyState, Options } from 'react-use-websocket';

const reconnectInterval = 5000;

function useSocket(
  url: string,
  options?: Options,
  isConnect = true,
  pingPong = true
) {
  const didUnmount = useRef(false);
  const [socketStatus, setSocketStatus] = useState('reconnect');
  const { filter, ...rest } = options || {};

  const {
    sendMessage,
    sendJsonMessage,
    lastMessage,
    lastJsonMessage,
    readyState,
    getWebSocket
  } = useWebSocket(
    url,
    {
      share: true,
      reconnectAttempts: 120,
      reconnectInterval,
      onOpen: () => console.log('WebSocket connection opened.'),
      shouldReconnect: () => {
        return didUnmount.current === false;
      },
      onReconnectStop: () => {
        setSocketStatus('disconnect');
        console.log('WebSocket connection closed.');
      },
      ...(filter
        ? {
            filter: message => {
              try {
                return options?.filter ? options?.filter(message) : false;
              } catch (error) {
                console.log('SomeThing Is Wrong With Socket Message');
                return false;
              }
            }
          }
        : {}),
      ...rest
    },
    isConnect
  );

  useEffect(() => {
    return () => {
      didUnmount.current = true;
    };
  }, []);

  useEffect(() => {
    const connectionStatus = {
      [ReadyState.CONNECTING]: 'reconnect',
      [ReadyState.OPEN]: 'connected',
      [ReadyState.CLOSING]: 'reconnect',
      [ReadyState.CLOSED]: 'reconnect',
      [ReadyState.UNINSTANTIATED]: 'uninstantiated'
    }[readyState];

    setSocketStatus(connectionStatus);
  }, [readyState]);

  const isConnected = useMemo(
    () => socketStatus === 'connected',
    [socketStatus]
  );

  const subscribe = useCallback((params: string[], id: string | number) => {
    sendMessage(
      JSON.stringify({
        method: 'SUBSCRIBE',
        params,
        id
      })
    );
  }, []);

  const unsubscribe = useCallback((params: string[], id: string | number) => {
    sendMessage(
      JSON.stringify({
        method: 'UNSUBSCRIBE',
        params,
        id
      })
    );
  }, []);

  useEffect(() => {
    if (isConnected && lastMessage?.data === '2' && pingPong) {
      sendMessage(JSON.stringify(3));
    }
  }, [isConnected, lastMessage]);

  return {
    status: socketStatus,
    sendMessage,
    sendJsonMessage,
    lastMessage,
    lastJsonMessage,
    readyState,
    getWebSocket,
    isConnected,
    subscribe,
    unsubscribe
  };
}

export default useSocket;
